'use client'

import { createGlobalAlert } from '@/lib/services/actions/global-alerts'
import { AlertSeverity, AlertType } from '@/lib/store/core/alerts'
import {
  Checkbox,
  Modal,
  TextArea
} from '@heroui/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/form-elements/Input'
import { CompatSelect as Select, ListBoxItem } from '@/components/ui/compat-select'
import { showToast } from '@/lib/utils/toast'
import { useState } from 'react'

interface GlobalAlertModalProps {
  isOpen: boolean
  onClose: () => void
}

export function GlobalAlertModal({ isOpen, onClose }: GlobalAlertModalProps) {
  const [message, setMessage] = useState('')
  const [dismissible, setDismissible] = useState(true)
  const [severity, setSeverity] = useState<AlertSeverity>('info')
  const [type, setType] = useState<AlertType>('system')
  const [autoHideDuration, setAutoHideDuration] = useState<string>('')
  const [linkText, setLinkText] = useState('')
  const [linkUrl, setLinkUrl] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const alertTypes: AlertType[] = [
    'system',
    'booking',
    'payment',
    'availability',
    'promotion',
    'security',
    'maintenance',
    'service'
  ]

  const alertSeverities: AlertSeverity[] = ['info', 'success', 'warning', 'error']

  const handleSubmit = async () => {
    if (!message.trim()) return

    setIsSubmitting(true)
    setError(null)

    try {
      const alertData = {
        message: message.trim(),
        type,
        severity,
        dismissible,
        autoHideDuration: autoHideDuration ? parseInt(autoHideDuration, 10) : undefined,
        link: linkText && linkUrl ? { text: linkText, url: linkUrl } : undefined
      }

      await createGlobalAlert(alertData)

      // Show success message
      showToast({
        title: 'Global alert created successfully',
        color: 'success',
        timeout: 3000
      })

      // Reset form and close modal
      resetForm()
      onClose()
    } catch (error) {
      console.error('Failed to create global alert:', error)
      setError(error instanceof Error ? error.message : 'Failed to create global alert. Please try again.')
      showToast({
        title: 'Failed to create global alert',
        color: 'danger',
        timeout: 5000
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setMessage('')
    setDismissible(true)
    setSeverity('info')
    setType('system')
    setAutoHideDuration('')
    setLinkText('')
    setLinkUrl('')
    setError(null)
  }

  return (
    <Modal.Backdrop
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          resetForm()
          onClose()
        }
      }}
      variant='blur'
    >
      <Modal.Container>
        <Modal.Dialog>
        <Modal.Header className='flex flex-col gap-1'>Create Global Alert</Modal.Header>
        <Modal.Body>
          {error && <div className='mb-4 p-3 bg-danger-50 text-danger-600 rounded-md text-sm'>{error}</div>}

          <TextArea
            label='Alert Message'
            placeholder='Enter alert message'
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            color='default'
            required
            rows={3}
          />

          <div className='grid grid-cols-2 gap-4'>
            <Select
              label='Type'
              placeholder='Select alert type'
              selectedKeys={[type]}
              onChange={(e) => setType(e.target.value as AlertType)}
            >
              {alertTypes.map((type) => (
                <ListBoxItem key={type} textValue={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </ListBoxItem>
              ))}
            </Select>

            <Select
              label='Severity'
              placeholder='Select alert severity'
              selectedKeys={[severity]}
              onChange={(e) => setSeverity(e.target.value as AlertSeverity)}
            >
              {alertSeverities.map((severity) => (
                <ListBoxItem key={severity} textValue={severity}>
                  {severity.charAt(0).toUpperCase() + severity.slice(1)}
                </ListBoxItem>
              ))}
            </Select>
          </div>

          <Input
            type='number'
            label='Auto-hide Duration (ms)'
            placeholder='Leave empty to not auto-hide'
            value={autoHideDuration}
            onChange={(e) => setAutoHideDuration(e.target.value)}
          />

          <Checkbox isSelected={dismissible} onChange={setDismissible}>
            <Checkbox.Content>
              <Checkbox.Control>
                <Checkbox.Indicator />
              </Checkbox.Control>
              Dismissible by user
            </Checkbox.Content>
          </Checkbox>

          <div className='space-y-4'>
            <p className='text-sm font-medium'>Link (Optional)</p>
            <div className='grid grid-cols-2 gap-4'>
              <Input
                label='Link Text'
                placeholder='Click here'
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
              />
              <Input
                label='URL'
                placeholder='https://example.com'
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='ghost'
            onPress={() => {
              resetForm()
              onClose()
            }}
          >
            Cancel
          </Button>
          <Button variant='primary' onPress={handleSubmit} isLoading={isSubmitting} isDisabled={!message.trim()}>
            Create Alert
          </Button>
        </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}

export default GlobalAlertModal
